import { memo, useMemo, useState } from 'react';
import { CheckCircle2, Clock, MousePointerClick, Search, Wrench, X } from 'lucide-react';
import { Badge } from '../ui/Badge';
import type { HerramientaCatalogo } from '../../lib/composicion';
import { useArrastreCompositor } from '../../hooks/useArrastre';
import { DetalleHerramienta } from './DetalleHerramienta';

/**
 * Catálogo de herramientas del compositor (aside izquierdo). Agrupa por
 * familia en el orden del arco Empatizar→Definir→Idear→Prototipar→Testear
 * (familias fuera del arco al final). La selección es POR CLIC (primera
 * clase): tocás una tarjeta y luego «Agregar aquí» en la sesión destino.
 * El drag nativo (Lote 2b) es aditivo: también podés arrastrar la tarjeta
 * hasta la sesión.
 */

const ORDEN_ARCO = ['empatizar', 'definir', 'idear', 'prototipar', 'testear'];

const ETIQUETA_ARCO: Record<string, string> = {
  empatizar: 'Empatizar',
  definir: 'Definir',
  idear: 'Idear',
  prototipar: 'Prototipar',
  testear: 'Testear',
};

function claveFamilia(familia: string | null | undefined): string {
  return (familia ?? '').trim().toLowerCase() || 'otras';
}

/** Comparación sin acentos ni mayúsculas («critica» encuentra «Crítica»). */
function normalizar(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function ordenFamilia(clave: string): number {
  const i = ORDEN_ARCO.indexOf(clave);
  return i === -1 ? ORDEN_ARCO.length : i;
}

/** Tipos de captura del manifiesto (v2 `capturas[]`, con alias v1 `captura`). */
function tiposCaptura(h: HerramientaCatalogo): string[] {
  const v2 = (h.capturas ?? [])
    .map((c) => c.tipo)
    .filter((t): t is string => Boolean(t));
  if (v2.length > 0) return [...new Set(v2)];
  const v1 = typeof h.captura === 'string' ? h.captura : h.captura?.tipo;
  return v1 ? [v1] : [];
}

interface GrupoFamilia {
  clave: string;
  etiqueta: string;
  items: HerramientaCatalogo[];
}

interface CatalogoHerramientasProps {
  herramientas: HerramientaCatalogo[];
  cargando: boolean;
  /** Código de la herramienta seleccionada (o null). */
  seleccion: string | null;
  /** Selecciona (código) o limpia (null) la herramienta en mano. */
  onSeleccion: (codigo: string | null) => void;
}

export const CatalogoHerramientas = memo(function CatalogoHerramientas({
  herramientas,
  cargando,
  seleccion,
  onSeleccion,
}: CatalogoHerramientasProps) {
  // Buscador + filtro por familia (deuda UX declarada: «el catálogo de 300px
  // sin buscador no escala» — con 70+ herramientas es indispensable).
  const [busqueda, setBusqueda] = useState('');
  const [familiaFiltro, setFamiliaFiltro] = useState<string | null>(null);

  const familiasPresentes = useMemo(() => {
    const claves = new Set(herramientas.map((h) => claveFamilia(h.familia)));
    return [...claves].sort(
      (a, b) => ordenFamilia(a) - ordenFamilia(b) || a.localeCompare(b, 'es'),
    );
  }, [herramientas]);

  const visibles = useMemo(() => {
    const aguja = normalizar(busqueda.trim());
    return herramientas.filter((h) => {
      if (familiaFiltro && claveFamilia(h.familia) !== familiaFiltro) return false;
      if (!aguja) return true;
      return (
        normalizar(h.nombre).includes(aguja) || normalizar(h.codigo).includes(aguja)
      );
    });
  }, [herramientas, busqueda, familiaFiltro]);

  const grupos = useMemo<GrupoFamilia[]>(() => {
    const mapa = new Map<string, GrupoFamilia>();
    for (const h of visibles) {
      const clave = claveFamilia(h.familia);
      let grupo = mapa.get(clave);
      if (!grupo) {
        const etiqueta =
          ETIQUETA_ARCO[clave] ??
          (clave === 'otras' ? 'Otras herramientas' : (h.familia ?? '').trim());
        grupo = { clave, etiqueta, items: [] };
        mapa.set(clave, grupo);
      }
      grupo.items.push(h);
    }
    return [...mapa.values()].sort(
      (a, b) =>
        ordenFamilia(a.clave) - ordenFamilia(b.clave) ||
        a.etiqueta.localeCompare(b.etiqueta, 'es'),
    );
  }, [visibles]);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-content-muted">
          <Wrench className="h-4 w-4" aria-hidden="true" />
          Catálogo de herramientas
        </h2>
        <p className="mt-1.5 flex items-start gap-1.5 font-sans text-xs text-content-muted">
          <MousePointerClick className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Tocá una herramienta y después «Agregar aquí» en la sesión donde va.
          También podés arrastrarla hasta la sesión.
        </p>
      </div>

      {/* Buscador + filtro por familia (con 70+ herramientas, indispensable). */}
      {!cargando && herramientas.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted"
              aria-hidden="true"
            />
            <input
              type="search"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar herramienta…"
              aria-label="Buscar herramienta por nombre"
              className="min-h-touch w-full rounded-md border border-border-strong bg-surface-sunken pl-8 pr-8 font-sans text-sm text-content placeholder:text-content-muted focus-visible:outline-none focus-visible:shadow-focus"
            />
            {busqueda && (
              <button
                type="button"
                onClick={() => setBusqueda('')}
                aria-label="Limpiar la búsqueda"
                className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded text-content-muted hover:text-content focus-visible:outline-none focus-visible:shadow-focus"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Filtrar por familia">
            <FiltroFamilia
              etiqueta="Todas"
              activa={familiaFiltro === null}
              onClick={() => setFamiliaFiltro(null)}
            />
            {familiasPresentes.map((clave) => (
              <FiltroFamilia
                key={clave}
                etiqueta={ETIQUETA_ARCO[clave] ?? (clave === 'otras' ? 'Otras' : clave)}
                activa={familiaFiltro === clave}
                onClick={() => setFamiliaFiltro(familiaFiltro === clave ? null : clave)}
              />
            ))}
          </div>
          {(busqueda || familiaFiltro) && (
            <p className="font-sans text-xs text-content-muted" aria-live="polite">
              Mostrando {visibles.length} de {herramientas.length} herramientas.
            </p>
          )}
        </div>
      )}

      {cargando ? (
        <p className="font-sans text-sm text-content-muted">Cargando el catálogo…</p>
      ) : herramientas.length === 0 ? (
        <p className="font-sans text-sm text-content-muted">
          Todavía no hay herramientas activas en el catálogo.
        </p>
      ) : grupos.length === 0 ? (
        <p className="font-sans text-sm text-content-muted">
          Ninguna herramienta coincide con tu búsqueda. Probá con otra palabra o
          quitá el filtro.
        </p>
      ) : (
        grupos.map((grupo) => (
          <section key={grupo.clave} className="flex flex-col gap-2">
            <h3 className="font-display text-xs font-semibold uppercase tracking-wide text-cyan">
              {grupo.etiqueta}
            </h3>
            <ul className="flex flex-col gap-2">
              {grupo.items.map((h) => (
                <li key={h.codigo} className="flex flex-col gap-1">
                  <TarjetaHerramienta
                    herramienta={h}
                    activa={h.codigo === seleccion}
                    onToggle={() =>
                      onSeleccion(h.codigo === seleccion ? null : h.codigo)
                    }
                  />
                  {/* Pedagogía (2b): la seleccionada expone su XIA completo —
                      el facilitador entiende QUÉ agrega antes de agregarlo. */}
                  {h.codigo === seleccion && (
                    <DetalleHerramienta herramienta={h} abiertoInicial />
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
});

function FiltroFamilia({
  etiqueta,
  activa,
  onClick,
}: {
  etiqueta: string;
  activa: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={activa}
      onClick={onClick}
      className={`rounded-full border px-2.5 py-1 font-sans text-xs font-medium transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus ${
        activa
          ? 'border-primary bg-surface-sunken text-content'
          : 'border-border bg-surface-raised text-content-muted hover:border-border-strong'
      }`}
    >
      {etiqueta}
    </button>
  );
}

function TarjetaHerramienta({
  herramienta,
  activa,
  onToggle,
}: {
  herramienta: HerramientaCatalogo;
  activa: boolean;
  onToggle: () => void;
}) {
  const capturas = tiposCaptura(herramienta);
  // Drag aditivo (Lote 2b): el clic de arriba sigue siendo primera clase.
  const arrastre = useArrastreCompositor();
  const enVuelo =
    arrastre?.item?.tipo === 'herramienta' &&
    arrastre.item.codigo === herramienta.codigo;
  return (
    <button
      type="button"
      aria-pressed={activa}
      onClick={onToggle}
      draggable={arrastre != null}
      onDragStart={arrastre?.arrastrarHerramienta(herramienta.codigo)}
      onDragEnd={arrastre?.terminarArrastre}
      /* Con el dedo el arrastre nativo no ocurre: se sostiene la herramienta un
         cuarto de segundo y se levanta. Antes de eso el toque sigue siendo un
         toque —que agrega la herramienta— y el deslizamiento sigue
         desplazando la lista. */
      onPointerDown={arrastre?.puntero.tomar(
        { tipo: 'herramienta', codigo: herramienta.codigo },
        herramienta.nombre,
      )}
      className={`w-full rounded-md border px-3 py-2.5 text-left transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus ${
        activa
          ? 'border-primary bg-surface-raised'
          : 'border-border bg-surface-raised hover:border-border-strong'
      } ${arrastre ? 'cursor-grab active:cursor-grabbing' : ''} ${
        enVuelo ? 'opacity-40' : ''
      }`}
    >
      <span className="flex items-start justify-between gap-2">
        <span className="min-w-0 font-sans text-sm font-semibold text-content">
          {herramienta.nombre}
        </span>
        {activa && (
          <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        )}
      </span>
      <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {herramienta.duracion_min != null && (
          <span className="flex items-center gap-1 font-sans text-xs tabular-nums text-content-muted">
            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
            {herramienta.duracion_min} min
          </span>
        )}
        {capturas.map((tipo) => (
          <Badge key={tipo} variant="neutral">
            {tipo}
          </Badge>
        ))}
      </span>
    </button>
  );
}
